import type { AxiosResponse } from 'axios';
import type { Core, Data } from '@strapi/types';
import { useMutation } from '@tanstack/react-query';
import { useTranslations } from '@power-core/toolkit';
import { useAppContext } from '@/modules/app-context/use-app-context';
import { useApi } from '@/modules/utils/custom-hooks/use-api';
import { useOpenDocument } from './use-open-document';
import {
	duplicateProjectDocumentApi,
	openPreviousVersionApi,
	moveProjectDocumentApi,
	createProjectDocumentApi,
	updateProjectDocumentApi,
	updateProjectDocumentStatusApi,
	deleteProjectDocumentApi,
	uploadProjectDocumentFileApi,
} from '../api/document.api';

export const useDocumentApi = () => {
	const t = useTranslations();
	const { project, showToast } = useAppContext();
	const openDocument = useOpenDocument();

	const { onSuccess } = useApi({ queryKey: ['GET_PROJECT_DOCUMENTS_LIST', project?.documentId] });
	const { onSuccess: onGetHistorySuccess } = useApi({
		queryKey: ['GET_FILTERED_PROJECT_DOCUMENTS_LIST', project?.documentId],
	});
	const { onSuccess: onGetProjectFiles } = useApi({ queryKey: ['GET_PROJECT_FILES', project?.documentId] });
	const { onSuccess: onGetProjectListSuccess } = useApi({ queryKey: ['GET_PROJECT_LIST'] });

	const openDocumentOnCreate = (
		response: AxiosResponse<Core.Helpers.Controllers.GetResponseBody<'api::project-document.project-document', 'duplicateProjectDocument'>>,
	) => {
		const { documentTypeName, projectDocumentId: documentId, fileDocumentId } = response.data;

		if (fileDocumentId) {
			openDocument({ documentId, documentTypeName });
		}
		onSuccess();
		onGetHistorySuccess();
	};

	const openPreviousVersion = useMutation({
		mutationKey: ['[POST]:CREATE_PROJECT_DOCUMENT'],
		mutationFn: (body: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'openPreviousVersion'>) =>
			openPreviousVersionApi(body),
		onSuccess: () => onSuccess(),
	});

	const duplicateProjectDocument = useMutation({
		mutationKey: ['[POST]:DUPLICATE_PROJECT_DOCUMENT'],
		mutationFn: ({ documentId }: { documentId: Pick<Data.Entity<'api::project.project'>, 'documentId'>['documentId'] }) =>
			duplicateProjectDocumentApi({ documentId }),
		onSuccess: (response) => openDocumentOnCreate(response),
		onError: () => showToast({ message: t('T_ERROR_DOCUMENT_DUPLICATION'), variant: 'error' }),
	});

	const moveProjectDocument = useMutation({
		mutationKey: ['[POST]:MOVE_PROJECT_DOCUMENT'],
		mutationFn: ({
			documentId,
			projectDocumentId,
		}: {
			documentId: Pick<Data.Entity<'api::project-document.project-document'>, 'documentId'>['documentId'];
			projectDocumentId: Pick<Data.Entity<'api::project.project'>, 'documentId'>['documentId'];
		}) => moveProjectDocumentApi({ documentId, body: { projectDocumentId } }),
		onSuccess: () => {
			onGetProjectListSuccess();
			showToast({ message: t('T_SUCCESS_DOCUMENT_MOVED'), variant: 'success' });
		},
		onError: () => showToast({ message: t('T_ERROR_MOVING_DOCUMENT'), variant: 'error' }),
	});

	const createProjectDocument = useMutation({
		mutationKey: ['[POST]:CREATE_PROJECT_DOCUMENT'],
		mutationFn: ({
			documentId,
			body,
		}: {
			documentId: Pick<Data.Entity<'api::project.project'>, 'documentId'>['documentId'];
			body: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'create'>;
		}) => createProjectDocumentApi({ documentId, body }),
		onSuccess: () => onSuccess('T_SUCCESS_DOCUMENT_CREATED'),
	});

	const updateProjectDocument = useMutation({
		mutationKey: ['[PUT]:UPDATE_PROJECT_DOCUMENT'],
		mutationFn: ({
			projectDocumentId,
			body,
		}: {
			projectDocumentId: Data.Entity<'api::project-document.project-document'>['documentId'];
			body: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'update'>;
		}) => updateProjectDocumentApi({ projectDocumentId, body }),
		onSuccess: () => {
			onSuccess();
			onGetHistorySuccess();
			onGetProjectFiles();
		},
	});

	const updateProjectDocumentStatus = useMutation({
		mutationKey: ['[PUT]:UPDATE_PROJECT_DOCUMENT_STATUS'],
		mutationFn: ({
			projectDocumentId,
			body,
		}: {
			projectDocumentId: Data.Entity<'api::project-document.project-document'>['documentId'];
			body: Core.Helpers.Controllers.GetRequestBody<'api::project-document.project-document', 'updateStatus'>;
		}) => updateProjectDocumentStatusApi({ projectDocumentId, body }),
		onSuccess: () => onSuccess(),
	});

	const deleteProjectDocument = useMutation({
		mutationKey: ['[DELETE]:DELETE_PROJECT_DOCUMENT'],
		mutationFn: ({ projectDocumentId }: { projectDocumentId: Data.Entity<'api::project-document.project-document'>['documentId'] }) => {
			return deleteProjectDocumentApi({ projectDocumentId });
		},
		onSuccess: () => {
			onGetProjectFiles();
			onGetHistorySuccess();
			onSuccess('T_SUCCESS_DOCUMENT_DELETED');
		},
	});

	const uploadProjectDocumentFile = useMutation({
		mutationKey: ['[POST]:UPLOAD_PROJECT_DOCUMENT_FILE'],
		mutationFn: ({
			projectId,
			body,
		}: {
			projectId: Pick<Data.Entity<'api::project.project'>, 'documentId'>['documentId'];
			body: FormData;
		}) => uploadProjectDocumentFileApi({ projectId, body }),
		onSuccess: (response) => openDocumentOnCreate(response),
		onError: () => showToast({ message: t('T_ERROR_UPLOADING_FILE'), variant: 'error' }),
	});

	const isPending =
		createProjectDocument.isPending ||
		deleteProjectDocument.isPending ||
		openPreviousVersion.isPending ||
		duplicateProjectDocument.isPending ||
		moveProjectDocument.isPending ||
		updateProjectDocument.isPending ||
		updateProjectDocumentStatus.isPending ||
		uploadProjectDocumentFile.isPending;

	return {
		openPreviousVersion,
		duplicateProjectDocument,
		moveProjectDocument,
		createProjectDocument,
		updateProjectDocument,
		updateProjectDocumentStatus,
		deleteProjectDocument,
		uploadProjectDocumentFile,
		isPending,
	};
};
